import React, { useState, useEffect, useCallback } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, Check, Tag, AlertTriangle, Archive, CheckCircle2, XCircle, Send, Loader2, Settings } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { authHeaders as getAuthHeaders } from '../../utils/unifiedAuth';
import api from '../../utils/api';


export default function AdminPositioningTags() {
  const navigate = useNavigate();

  const [tags, setTags] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [tab, setTab] = useState('catalog');
  const [loading, setLoading] = useState(true);

  // Add form
  const [showAdd, setShowAdd] = useState(false);
  const [newSlug, setNewSlug] = useState('');
  const [newDisplay, setNewDisplay] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [adding, setAdding] = useState(false);

  // P6H — metadata editor modal state
  const [metaEditTag, setMetaEditTag] = useState(null); // {slug, display, category, status, ranking_weight, ...}
  const [metaSaving, setMetaSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tagRes, sugRes] = await Promise.all([
        api.get('/admin/positioning-tags'),
        api.get('/admin/positioning-tags/suggestions'),
      ]);
      setTags(tagRes.data.tags || []);
      setSuggestions(sugRes.data.suggestions || []);
    } catch (e) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpsert = async () => {
    if (!newSlug.trim() || !newDisplay.trim()) return;
    setAdding(true);
    try {
      await api.post('/admin/positioning-tags/upsert', { slug: newSlug.trim(), display: newDisplay.trim(), category: newCategory.trim(), status: 'active' });
      toast.success('Tag added');
      setShowAdd(false); setNewSlug(''); setNewDisplay(''); setNewCategory('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || 'Add failed'); }
    finally { setAdding(false); }
  };

  const handleDisable = async (slug) => {
    try {
      await api.post('/admin/positioning-tags/disable', { slug });
      toast.success(`"${slug}" disabled`); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || 'Disable failed'); }
  };

  const handleEnable = async (slug) => {
    const tag = tags.find(t => t.slug === slug);
    if (!tag) return;
    try {
      await api.post('/admin/positioning-tags/upsert', { slug, display: tag.display, category: tag.category || '', status: 'active' });
      toast.success(`"${slug}" re-enabled`); fetchData();
    } catch (e) { toast.error(e.message); }
  };

  const handleApprove = async (id) => {
    try {
      await api.post(`/admin/positioning-tags/suggestions/${id}/approve`);
      toast.success('Approved and added to catalog'); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || 'Approve failed'); }
  };

  const handleReject = async (id) => {
    try {
      await api.post(`/admin/positioning-tags/suggestions/${id}/reject`);
      toast.success('Rejected'); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || 'Reject failed'); }
  };

  // P6H — Save metadata edits for a tag
  const handleSaveMetadata = async () => {
    if (!metaEditTag) return;
    setMetaSaving(true);
    try {
      await api.post('/admin/positioning-tags/upsert', {
        slug: metaEditTag.slug,
        display: metaEditTag.display,
        category: metaEditTag.category || '',
        status: metaEditTag.status || 'active',
        ranking_weight: Number.isFinite(Number(metaEditTag.ranking_weight)) ? Number(metaEditTag.ranking_weight) : 0,
        is_filterable: !!metaEditTag.is_filterable,
        is_searchable: !!metaEditTag.is_searchable,
        is_premium_signal: !!metaEditTag.is_premium_signal,
        sort_order: Number.isFinite(Number(metaEditTag.sort_order)) ? Number(metaEditTag.sort_order) : 99,
      });
      toast.success(`"${metaEditTag.slug}" metadata saved`);
      setMetaEditTag(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setMetaSaving(false);
    }
  };

  const active = tags.filter(t => t.status === 'active');
  const deleted = tags.filter(t => t.status === 'deleted');
  const pendingSugs = suggestions.filter(s => s.status === 'pending');

  const autoSlug = (text) => text.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 64);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/treatx-admin')} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <h1 className="text-xl font-semibold text-[#122033]">Positioning Tags</h1>
            <Badge className="bg-slate-100 text-slate-600 text-[10px]">{active.length} active</Badge>
          </div>
          <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white text-xs gap-1" onClick={() => setShowAdd(true)} data-testid="add-tag-btn">
            <Plus className="h-3 w-3" /> Add Tag
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-4 border-b border-slate-200">
          <button
            onClick={() => setTab('catalog')}
            className={`pb-2 text-sm transition-colors ${tab === 'catalog' ? 'border-b-2 border-violet-500 text-violet-600 font-medium' : 'text-slate-400 hover:text-slate-600'}`}
            data-testid="tab-catalog"
          >
            <Tag className="h-3.5 w-3.5 inline mr-1" /> Catalog ({tags.length})
          </button>
          <button
            onClick={() => setTab('suggestions')}
            className={`pb-2 text-sm transition-colors ${tab === 'suggestions' ? 'border-b-2 border-violet-500 text-violet-600 font-medium' : 'text-slate-400 hover:text-slate-600'}`}
            data-testid="tab-suggestions"
          >
            <Send className="h-3.5 w-3.5 inline mr-1" /> Suggestions
            {pendingSugs.length > 0 && <Badge className="ml-1 bg-amber-100 text-amber-700 text-[9px]">{pendingSugs.length}</Badge>}
          </button>
        </div>

        {/* Add Form */}
        {showAdd && (
          <div className="mb-4 p-4 border border-violet-200 rounded-lg bg-white space-y-3" data-testid="add-tag-form">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] text-slate-400 uppercase">Display Name</label>
                <input
                  value={newDisplay} onChange={e => { setNewDisplay(e.target.value); setNewSlug(autoSlug(e.target.value)); }}
                  className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-violet-300 focus:outline-none"
                  placeholder="Premium Boutique" data-testid="add-tag-display"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-400 uppercase">Slug</label>
                <input
                  value={newSlug} onChange={e => setNewSlug(e.target.value)}
                  className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm font-mono focus:ring-1 focus:ring-violet-300 focus:outline-none"
                  placeholder="premium-boutique" data-testid="add-tag-slug"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-400 uppercase">Category</label>
                <input
                  value={newCategory} onChange={e => setNewCategory(e.target.value)}
                  className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-violet-300 focus:outline-none"
                  placeholder="positioning" data-testid="add-tag-category"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button size="sm" className="h-7 text-xs bg-violet-600 hover:bg-violet-700 text-white" onClick={handleUpsert} disabled={adding || !newSlug.trim() || !newDisplay.trim()} data-testid="add-tag-submit">
                {adding ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Add'}
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
        ) : tab === 'catalog' ? (
          <div className="space-y-2" data-testid="catalog-list">
            {tags.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No tags in catalog</p>}
            {/* Active tags */}
            {active.map(tag => (
              <div key={tag.slug} className="flex items-center justify-between px-4 py-2.5 bg-white border border-slate-200 rounded-lg" data-testid={`catalog-tag-${tag.slug}`}>
                <div className="flex items-center gap-3 min-w-0 flex-wrap">
                  <Badge className="bg-emerald-50 text-emerald-600 text-[10px] border border-emerald-200">active</Badge>
                  <span className="text-sm font-medium text-[#122033]">{tag.display}</span>
                  <span className="text-[10px] font-mono text-slate-400">{tag.slug}</span>
                  {tag.category && <Badge variant="outline" className="text-[9px] border-slate-200">{tag.category}</Badge>}
                  {/* P6H metadata badges */}
                  <span className="text-[10px] text-slate-500 font-mono" data-testid={`meta-weight-${tag.slug}`} title="ranking weight">w={tag.ranking_weight ?? '-'}</span>
                  <span className="text-[10px] text-slate-500 font-mono" data-testid={`meta-sort-${tag.slug}`} title="sort order">#{tag.sort_order ?? '-'}</span>
                  {tag.is_premium_signal && <Badge className="bg-violet-50 text-violet-600 text-[9px] border border-violet-200" data-testid={`meta-premium-${tag.slug}`}>premium</Badge>}
                  {!tag.is_filterable && <Badge className="bg-slate-100 text-slate-500 text-[9px] border border-slate-200" data-testid={`meta-nofilter-${tag.slug}`}>non-filterable</Badge>}
                  {!tag.is_searchable && <Badge className="bg-slate-100 text-slate-500 text-[9px] border border-slate-200" data-testid={`meta-nosearch-${tag.slug}`}>non-searchable</Badge>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500 hover:text-violet-600 hover:bg-violet-50" onClick={() => setMetaEditTag({ ...tag })} data-testid={`edit-meta-${tag.slug}`}>
                    <Settings className="h-3 w-3 mr-1" /> Metadata
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-amber-500 hover:text-amber-700 hover:bg-amber-50" onClick={() => handleDisable(tag.slug)} data-testid={`disable-${tag.slug}`}>
                    <Archive className="h-3 w-3 mr-1" /> Disable
                  </Button>
                </div>
              </div>
            ))}

            {/* Deleted tags */}
            {deleted.length > 0 && (
              <>
                <div className="pt-3 pb-1 flex items-center gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-slate-300" />
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">Deleted ({deleted.length})</span>
                </div>
                {deleted.map(tag => (
                  <div key={tag.slug} className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg opacity-60" data-testid={`catalog-tag-${tag.slug}`}>
                    <div className="flex items-center gap-3">
                      <Badge className="bg-slate-100 text-slate-400 text-[10px] border border-slate-200">deleted</Badge>
                      <span className="text-sm text-slate-500 line-through">{tag.display}</span>
                      <span className="text-[10px] font-mono text-slate-400">{tag.slug}</span>
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50" onClick={() => handleEnable(tag.slug)} data-testid={`enable-${tag.slug}`}>
                      <Check className="h-3 w-3 mr-1" /> Enable
                    </Button>
                  </div>
                ))}
              </>
            )}
          </div>
        ) : (
          /* Suggestions Tab */
          <div className="space-y-2" data-testid="suggestions-list">
            {suggestions.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No suggestions yet</p>}
            {suggestions.map(sug => (
              <div key={sug.suggestion_id} className="px-4 py-3 bg-white border border-slate-200 rounded-lg" data-testid={`suggestion-${sug.suggestion_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-[#122033]">{sug.proposed_display}</span>
                      <span className="text-[10px] font-mono text-slate-400">{sug.proposed_slug}</span>
                      <Badge className={`text-[9px] ${
                        sug.status === 'pending' ? 'bg-amber-50 text-amber-600 border-amber-200' :
                        sug.status === 'approved' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                        'bg-red-50 text-red-500 border-red-200'
                      }`}>{sug.status}</Badge>
                    </div>
                    {sug.notes && <p className="text-[11px] text-slate-400">{sug.notes}</p>}
                    <p className="text-[9px] text-slate-300 mt-0.5">From: {sug.clinic_id} | {formatDateLocale(sug.created_at_utc)}</p>
                  </div>
                  {sug.status === 'pending' && (
                    <div className="flex gap-1.5 shrink-0 ml-3">
                      <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleApprove(sug.suggestion_id)} data-testid={`approve-${sug.suggestion_id}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 text-xs border-red-200 text-red-500 hover:bg-red-50" onClick={() => handleReject(sug.suggestion_id)} data-testid={`reject-${sug.suggestion_id}`}>
                        <XCircle className="h-3 w-3 mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* P6H — Metadata editor modal */}
      {metaEditTag && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="tag-meta-modal">
          <div className="bg-white rounded-md border border-slate-200 shadow-lg w-full max-w-md mx-3">
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-[#122033]">
                Metadata · <span className="font-mono text-[11px] text-slate-500">{metaEditTag.slug}</span>
              </h2>
              <button onClick={() => setMetaEditTag(null)} className="h-6 w-6 flex items-center justify-center rounded text-slate-400 hover:bg-slate-100" data-testid="tag-meta-close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Ranking Weight</span>
                  <input type="number" min="0" max="10" value={metaEditTag.ranking_weight ?? 0}
                    onChange={e => setMetaEditTag(t => ({ ...t, ranking_weight: e.target.value }))}
                    className="w-full text-[12px] font-mono border border-slate-200 rounded px-2 py-1.5 focus:ring-1 focus:ring-violet-300 focus:outline-none"
                    data-testid="tag-meta-weight" />
                </label>
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Sort Order</span>
                  <input type="number" value={metaEditTag.sort_order ?? 99}
                    onChange={e => setMetaEditTag(t => ({ ...t, sort_order: e.target.value }))}
                    className="w-full text-[12px] font-mono border border-slate-200 rounded px-2 py-1.5 focus:ring-1 focus:ring-violet-300 focus:outline-none"
                    data-testid="tag-meta-sort" />
                </label>
              </div>
              <label className="flex items-center gap-2 text-[12px] text-slate-700">
                <input type="checkbox" checked={!!metaEditTag.is_filterable}
                  onChange={e => setMetaEditTag(t => ({ ...t, is_filterable: e.target.checked }))}
                  data-testid="tag-meta-filterable" />
                Filterable (shows in search filter chips)
              </label>
              <label className="flex items-center gap-2 text-[12px] text-slate-700">
                <input type="checkbox" checked={!!metaEditTag.is_searchable}
                  onChange={e => setMetaEditTag(t => ({ ...t, is_searchable: e.target.checked }))}
                  data-testid="tag-meta-searchable" />
                Searchable (indexable by keyword search)
              </label>
              <label className="flex items-center gap-2 text-[12px] text-slate-700">
                <input type="checkbox" checked={!!metaEditTag.is_premium_signal}
                  onChange={e => setMetaEditTag(t => ({ ...t, is_premium_signal: e.target.checked }))}
                  data-testid="tag-meta-premium" />
                Premium signal (used by ranking heuristics)
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-slate-100 bg-slate-50/40">
              <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => setMetaEditTag(null)} disabled={metaSaving} data-testid="tag-meta-cancel">
                Cancel
              </Button>
              <Button size="sm" className="h-7 text-[11px] gap-1 bg-violet-600 hover:bg-violet-700 text-white" onClick={handleSaveMetadata} disabled={metaSaving} data-testid="tag-meta-save">
                {metaSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                Save metadata
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
